import { useEffect, useState } from 'react';

import { RoseTree } from '@tableau/data-structures';
import { NestedProjectBasicInfo, NestedProjectsTreeHelper } from '@tableau/nested-project-explorer';
import { SliceableHelper } from '../../ts/helpers/SliceableHelper';
import { DestinationProjectMapper } from '../../ts/mappers/DestinationProjectMapper';
import { DestinationProjects } from '../../projects/resources/DestinationProjects';
import { getSessionManagementServiceInstance, SessionManagementService } from '../../sessionManagement/SessionManagementService';

export function useProjects() {
  const [projects, setProjects] = useState<RoseTree<NestedProjectBasicInfo>[]>(null);
  useEffect(() => {
    getAllProjectsAsTrees().then(projectTree => {
      setProjects(projectTree);
    });
  }, []); // No dependencies, so empty array to prevent re-renders
  return projects;
}

// Testing whether it's better to abstract the ServiceLocator behind a hook for tests
export function getSiteName(): string {
  return getSessionManagementServiceInstance().sessionSiteName();
}

function getInitialProject(tree: RoseTree<NestedProjectBasicInfo>[], projectId: string) {
  if (projectId) {
    return getProjectById(tree, projectId);
  } else {
    return getDefaultProject(tree);
  }
}

function getDefaultProject(projectTree: RoseTree<NestedProjectBasicInfo>[]): NestedProjectBasicInfo {
  if (!projectTree) {
    return null;
  }
  const lowestProject = projectTree.reduce((lowestProject: RoseTree<NestedProjectBasicInfo>, project: RoseTree<NestedProjectBasicInfo>) =>
    Number(lowestProject.node.id) > Number(project.node.id) && project.node.nestingLevel === 1 ? project : lowestProject
  , projectTree[0]);

  return lowestProject?.node;
}

function getProjectById(tree: RoseTree<NestedProjectBasicInfo>[], projectId: string): NestedProjectBasicInfo {
  if (!tree) {
    return null;
  }
  let retProjectNode: NestedProjectBasicInfo;
  const projNode = tree.find(projectNode => {
    if (projectNode.node.id === projectId) {
      return true;
    } else if (projectNode.node.hasChildren) {
      projectNode.forEach(p => {
        if (p.id === projectId) {
          retProjectNode = p;
        }});
      return false;
    }
  });
  return retProjectNode ? retProjectNode : projNode?.node ?? null;
}

function getAllProjectsAsTrees(): ng.IPromise<RoseTree<NestedProjectBasicInfo>[]> {
  // Returns all projects as trees
  const contentType = 'project';
  const itemIds: string[] = [];
  const query = '';
  const projectsQuery = DestinationProjects.getQueryForSpecificSourceContentAndMatchText(contentType, itemIds, query);
  return SliceableHelper.fetchAll(projectsQuery).then(projects => {
    const destinationProjects = DestinationProjectMapper.map(projects.items);
    const nestedProjects = NestedProjectsTreeHelper.convertProjectListToTrees(destinationProjects);
    return nestedProjects;
  });
}

export default {
  getInitialProject,
};
